// components/clubs/MembersListModal.tsx
// -----------------------------------------------------------------------------
// Members list modal dialog matching mobile members screen structure.
// -----------------------------------------------------------------------------

import { useState, useMemo, useCallback } from "react";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { useNavigate } from "@tanstack/react-router";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
} from "@/components/ui/SidePanel";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  useFetchClubMembersQuery,
  useFetchUserProfilesByIdsQuery,
} from "@/api/clubsApi";
import type { UserProfile, Club } from "@/api/clubsApi";
import { userDisplayName } from "@tinyclub/shared/utils";

interface MembersListModalProps {
  club: Club;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MembersListModal({
  club,
  open,
  onOpenChange,
}: MembersListModalProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  // Handle member click - navigate to profile
  // Note: We don't close the modal here. The URL ?members param persists,
  // so when user presses back, they return to the modal-open state.
  const handleMemberClick = (userId: string) => {
    navigate({ to: "/profile/$userId", params: { userId } });
  };

  // Fetch all club members
  const { data: members, isLoading: membersLoading } = useFetchClubMembersQuery(
    { clubId: club.id!, includeUserProfiles: false },
    { skip: !club.id || !open }
  );

  // Get all member IDs
  const memberIds = useMemo(() => {
    if (!members) return [];
    return members.map((m) => m.user_id).filter(Boolean) as string[];
  }, [members]);

  // Fetch profiles for all members
  const { data: memberProfiles = [], isLoading: profilesLoading } =
    useFetchUserProfilesByIdsQuery(memberIds, {
      skip: memberIds.length === 0 || !open,
    });

  // Create a map of user ID to profile
  const profileMap = useMemo(() => {
    return new Map(memberProfiles.map((p) => [p.id, p]));
  }, [memberProfiles]);

  // Separate members into owners, cohosts, and regular members
  const { ownerProfiles, cohostProfiles, regularMemberProfiles } = useMemo(() => {
    const owners: UserProfile[] = [];
    const cohosts: UserProfile[] = [];
    const regularMembers: UserProfile[] = [];

    members?.forEach((member) => {
      const profile = profileMap.get(member.user_id);
      if (!profile) return;

      if (member.role === "owner") {
        owners.push(profile);
      } else if (member.role === "cohost") {
        cohosts.push(profile);
      } else {
        regularMembers.push(profile);
      }
    });

    return {
      ownerProfiles: owners,
      cohostProfiles: cohosts,
      regularMemberProfiles: regularMembers,
    };
  }, [members, profileMap]);

  // Filter function based on search query
  const filterProfiles = useCallback(
    (profiles: UserProfile[]) => {
      if (!searchQuery.trim()) return profiles;

      const query = searchQuery.toLowerCase();
      return profiles.filter((profile) => {
        const displayName = getDisplayName(profile).toLowerCase();
        const username = profile.username?.toLowerCase() || "";
        return displayName.includes(query) || username.includes(query);
      });
    },
    [searchQuery]
  );

  // Apply filtering to each section
  const filteredHosts = useMemo(
    () => filterProfiles([...ownerProfiles, ...cohostProfiles]),
    [ownerProfiles, cohostProfiles, filterProfiles]
  );
  const filteredMembers = useMemo(
    () => filterProfiles(regularMemberProfiles),
    [regularMemberProfiles, filterProfiles]
  );

  // Build flat list for keyboard navigation (hosts first, then members)
  const allFilteredProfiles = useMemo(
    () => [...filteredHosts, ...filteredMembers],
    [filteredHosts, filteredMembers]
  );

  const { highlightedIndex, onKeyDown } = useKeyboardNavigation({
    items: allFilteredProfiles,
    onSelect: (profile) => handleMemberClick(profile.id),
    enabled: !!searchQuery.trim(),
  });

  const isLoading = membersLoading || profilesLoading;
  const hasResults = filteredHosts.length > 0 || filteredMembers.length > 0;

  // Get role for a profile
  const getRoleForProfile = (profileId: string): string | null => {
    const member = members?.find((m) => m.user_id === profileId);
    if (member?.role === "owner") return "Owner";
    if (member?.role === "cohost") return "Co-host";
    return null;
  };

  // Reset search when modal closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSearchQuery("");
    }
    onOpenChange(newOpen);
  };

  return (
    <SidePanel open={open} onOpenChange={handleOpenChange}>
      <SidePanelContent>
        <SidePanelHeader title="Members" onClose={() => handleOpenChange(false)} />

        {/* Search Bar */}
        <div className="flex-shrink-0 px-5 pb-4">
          <div className="flex items-center gap-3 px-4 py-3 rounded-full border border-border-tertiary bg-surface">
            <Icon name="search" size={16} className="text-text-secondary" />
            <input
              type="text"
              placeholder="Search members"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={onKeyDown}
              className="flex-1 bg-transparent text-body-2 text-text-primary placeholder:text-text-secondary focus:outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="p-1 hover:opacity-70 transition-opacity"
              >
                <Icon name="xmark" size={16} className="text-text-tertiary" />
              </button>
            )}
          </div>
        </div>

        {/* Scrollable Content */}
        <SidePanelBody className="pt-0">
          {isLoading ? (
            <MembersListSkeleton />
          ) : !hasResults && searchQuery ? (
            <div className="text-center py-12">
              <p className="text-body-2 text-text-secondary">
                No members matching "{searchQuery}"
              </p>
            </div>
          ) : (
            <div className="space-y-6 pb-4">
              {/* Hosts Section */}
              {filteredHosts.length > 0 && (
                <MemberSection
                  title="Hosts"
                  profiles={filteredHosts}
                  getRoleForProfile={getRoleForProfile}
                  onMemberClick={handleMemberClick}
                  startIndex={0}
                  highlightedIndex={highlightedIndex}
                />
              )}

              {/* Members Section */}
              {filteredMembers.length > 0 && (
                <MemberSection
                  title="Members"
                  profiles={filteredMembers}
                  getRoleForProfile={getRoleForProfile}
                  onMemberClick={handleMemberClick}
                  startIndex={filteredHosts.length}
                  highlightedIndex={highlightedIndex}
                />
              )}
            </div>
          )}
        </SidePanelBody>
      </SidePanelContent>
    </SidePanel>
  );
}

// Member section component
function MemberSection({
  title,
  profiles,
  getRoleForProfile,
  onMemberClick,
  startIndex = 0,
  highlightedIndex = -1,
}: {
  title: string;
  profiles: UserProfile[];
  getRoleForProfile: (profileId: string) => string | null;
  onMemberClick: (userId: string) => void;
  startIndex?: number;
  highlightedIndex?: number;
}) {
  return (
    <section>
      <h2 className="text-h6 text-text-primary mb-3">{title}</h2>
      <div className="space-y-1">
        {profiles.map((profile, i) => (
          <MemberRow
            key={profile.id}
            profile={profile}
            role={getRoleForProfile(profile.id)}
            onClick={() => onMemberClick(profile.id)}
            navIndex={startIndex + i}
            highlighted={startIndex + i === highlightedIndex}
          />
        ))}
      </div>
    </section>
  );
}

// Individual member row
function MemberRow({
  profile,
  role,
  onClick,
  navIndex,
  highlighted = false,
}: {
  profile: UserProfile;
  role: string | null;
  onClick: () => void;
  navIndex?: number;
  highlighted?: boolean;
}) {
  const displayName = getDisplayName(profile);

  return (
    <button
      onClick={onClick}
      data-nav-index={navIndex}
      className={`w-full flex items-center gap-3 p-3 rounded-xl hover:bg-surface transition-colors cursor-pointer text-left${highlighted ? " bg-surface" : ""}`}
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-border-tertiary">
        {profile.avatar_medium || profile.avatar_small ? (
          <img
            src={profile.avatar_medium || profile.avatar_small || ""}
            alt={displayName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-surface-1 flex items-center justify-center">
            <Icon name="user" size={16} className="text-text-tertiary" />
          </div>
        )}
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0">
        <p className="text-body-2-medium text-text-primary truncate">
          {displayName}
        </p>
      </div>

      {/* Role badge */}
      {role && (
        <span className="px-2 py-1 rounded-lg bg-surface-1 text-body-3 text-text-secondary flex-shrink-0">
          {role}
        </span>
      )}
    </button>
  );
}

// Skeleton loader
function MembersListSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-5 w-14 mb-3" />
        <div className="space-y-1">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-[56px] w-full rounded-xl" />
          ))}
        </div>
      </div>
      <div>
        <Skeleton className="h-5 w-20 mb-3" />
        <div className="space-y-1">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-[56px] w-full rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

// Helper to get display name - use shared utility
function getDisplayName(profile: UserProfile): string {
  return userDisplayName(profile);
}
